import { BaseContract } from '../contract';
import { Income } from '../income';
import { StorageFile } from '../media';
import { DocumentMeta, createDocumentMeta } from '../meta';
import { Media, MovieCurrency, RightholderRole, Territory, rightholderGroups, WaterfallMode } from '../static';
import { ExpenseType } from '../expense';
import { Term } from '../terms';
import { sortStatements, Statement } from './statement';
import { SupportedLanguages } from '../utils';
import { Movie } from '../movie';
import { defaultColor } from './graph';
import { Right } from './right';

export interface WaterfallPermissions {
  _meta?: DocumentMeta;
  id: string; // orgId
  // Define the rightholderIds (fake orgs) that this current org can impersonate. Only one rightholderId per org for now
  rightholderIds: string[];
  // Is the current org admin of the waterfall
  isAdmin: boolean;
}

export function createWaterfallPermissions(params: Partial<WaterfallPermissions> = {}): WaterfallPermissions {
  return {
    id: '',
    rightholderIds: [],
    isAdmin: false,
    ...params,
  }
}

export interface CombinedWith {
  versionId: string;
  // Should contain at least one of rightholderIds or rightIds
  rightholderIds?: string[];
  rightIds?: string[];
}

export interface Version {
  id: string;
  default: boolean;
  standalone: boolean;
  combined: boolean;
  name: string;
  description?: string;
  blockIds: string[];
  combinedWith: CombinedWith[];
}

export function createVersion(params: Partial<Version> = {}) {
  const version: Version = {
    id: '',
    default: true,
    standalone: false, // If true, indicates that this version does not share any rights or sources with other versions
    combined: false, // If true, combinedWith can be provided, if not, will act as a regular not default version
    name: '',
    blockIds: [],
    combinedWith: [],
    ...params
  }

  if (!version.name) version.name = version.id;
  return version;
}

export function orderVersions(versions: Version[]) {
  const defaultVersion = versions.find(v => v.default);
  const otherVersions = versions.filter(v => v.id !== defaultVersion?.id);

  const orderedVersions: Version[] = [];
  if (defaultVersion) orderedVersions.push(defaultVersion);
  if (defaultVersion && otherVersions.length) orderedVersions.push(...otherVersions.sort((a, b) => a.name.localeCompare(b.name)));
  return orderedVersions;
}

export function getDefaultVersion(waterfall: Waterfall) {
  return waterfall.versions.find(v => v.default);
}

export function hasDefaultVersion(waterfall: Waterfall) {
  return !!getDefaultVersion(waterfall);
}

export function getDefaultVersionId(waterfall: Waterfall) {
  return getDefaultVersion(waterfall)?.id;
}

export function isDefaultVersion(waterfall: Waterfall, versionId: string) {
  if (!versionId) return false;
  return getDefaultVersionId(waterfall) === versionId;
}

export function isStandaloneVersion(waterfall: Waterfall, versionId: string) {
  if (!versionId) return false;
  return waterfall.versions.find(v => v.id === versionId)?.standalone;
}

export interface WaterfallFile extends StorageFile {
  id: string; // Same as the WaterfallDocument id
  privacy: 'protected';
}

export function createWaterfallSource(params: Partial<WaterfallSource>): WaterfallSource {
  return {
    id: '',
    name: '',
    territories: [],
    medias: [],
    destinationId: '',
    version: {},
    shadow: false,
    ...params
  };
}

export function createWaterfallSourceLite(params: Partial<WaterfallSource>): WaterfallSourceLite {
  return {
    id: params?.id ?? '',
    name: params?.name ?? '',
  };
}

export function checkDestinationValidity(source: WaterfallSource, rights: Right[]) {
  if (!source.destinationId) return true;
  const right = rights.find(r => r.id === source.destinationId && !r.groupId);
  if (!right) return false;
  return true;
}

/**
 * Fetch versionned sources if versionId is provided, else return all sources
 * @param waterfall
 * @param versionId
 * @returns
 */
export function waterfallSources(waterfall: Waterfall, versionId?: string): WaterfallSource[] {
  if (!versionId) return waterfall.sources;
  const version = waterfall.versions.find(v => v.id === versionId);
  if (!version) return waterfall.sources;
  const sources = waterfall.sources;

  if (version.standalone) return sources.filter(s => s.version && s.version[version.id]);

  return sources.filter(r => !Object.values(r.version).some(v => v.standalone)).map(s => {
    if (!s.version) s.version = {};
    const destinationId = s.version[versionId] !== undefined ? s.version[versionId].destinationId : s.destinationId;
    return { ...s, destinationId };
  });
}

export function getIncomesSources(incomes: Income[], sources: WaterfallSource[]) {
  return Array.from(new Set(incomes.map(i => sources.find(s => !!i?.sourceId && s.id === i.sourceId))));
}

export function sourceName(sourceId: string, sources: WaterfallSource[]) {
  return sources.find(source => source.id === sourceId)?.name || '--';
}

/**
 * Defines sources that an income will be associated to.
 * row_all, us_svod etc ..
 */
export interface WaterfallSource extends WaterfallSourceLite {
  territories: Territory[];
  medias: Media[];
  destinationId: string; // The rightId this income will go to
  shadow: boolean;
  version: Record<string, { standalone?: true, destinationId?: string }>; // TODO #10633 is destinationId used ? 
}

export interface WaterfallSourceLite {
  id: string;
  name: string;
}

export interface WaterfallRightholder {
  id: string;
  name: string;
  roles: RightholderRole[];
  lockedVersionId?: string;
  siren?: string;
  color: string;
}

export type WaterfallEventMode = 'boolean' | 'number';
export interface WaterfallEvent {
  id: string;
  mode: WaterfallEventMode;
  // If mode is boolean
  checked?: boolean; // Required if mode is boolean

  // If mode is amount
  amount?: number; // Required if mode is number
  target?: number; // Goal amount (defined in conditions)
  prior?: number; // Amount declared before this event (read from state)
}

export function createWaterfallEvent(params: Partial<WaterfallEvent> = {}) {
  const event: Partial<WaterfallEvent> = { id: params.id || '' };

  if (params.checked !== undefined) {
    event.mode = 'boolean';
    event.checked = params.checked;
  } else if (params.amount !== undefined) {
    event.mode = 'number';
    event.amount = params.amount;
    event.target = params.target ?? 0;
    event.prior = params.prior ?? 0;
  } else {
    throw new Error('Missing data to create WaterfallEvent');
  }
  return event as WaterfallEvent;
}

export function computeEvents(statements: Statement[]) {
  const orderedStatements = sortStatements(statements);
  const statementEvents = orderedStatements.map(s => s.events).flat();
  const eventsIds = Array.from(new Set(statementEvents.map(e => e.id)));

  const computedEvents: WaterfallEvent[] = [];
  for (const eventId of eventsIds) {
    // Statements are ordered by date, so the first event found is the most recent one
    const event = statementEvents.find(e => e.id === eventId);
    computedEvents.push(event);
  }

  return computedEvents;
}

interface RcaData {
  title?: string;
  url: string;
  registrationNumber: string;
  registrationDate: Date;
}

export interface WaterfallRcaData extends RcaData {
  totalContracts: number;
  savedContracts: number;
  processingStatus?: 'processing' | 'error' | 'completed';
  error?: string;
}

export function createWaterfallRcaData(params: Partial<WaterfallRcaData>): WaterfallRcaData {
  return {
    title: '',
    url: '',
    registrationNumber: '',
    registrationDate: new Date(),
    totalContracts: 0,
    savedContracts: 0,
    processingStatus: 'processing',
    ...params,
  }
}

interface WaterfallContractRcaData extends RcaData {
  pageCount: number;
}

export function createWaterfallContractRcaData(params: Partial<WaterfallContractRcaData>): WaterfallContractRcaData {
  return {
    url: '',
    registrationNumber: '',
    registrationDate: new Date(),
    pageCount: 0,
    ...params,
  }
}

export interface TextBlock {
  text: string;
  confidence: number;
}

export interface StructuredPageData {
  pageNumber: number;
  blocks: TextBlock[];
}

type OcrStatus = 'completed' | 'error';

export interface StructuredData {
  pages: StructuredPageData[];
}

export interface OcrData {
  raw: string; /** The raw text extracted from the document */
  structuredData: StructuredData;
  status: OcrStatus;
  error?: string;
  date: Date;
}

export function createOcrData(params: Partial<OcrData> = {}): OcrData {
  return {
    raw: '',
    structuredData: {
      pages: [],
    },
    status: 'completed',
    date: new Date(),
    ...params
  };
}

export interface WaterfallTemplate {
  isTemplate: boolean;
  name: string;
  languages: SupportedLanguages[];
}

export function createWaterfallTemplate(params: Partial<WaterfallTemplate>): WaterfallTemplate {
  return {
    isTemplate: params?.isTemplate ?? false,
    name: params?.name ?? '',
    languages: params?.languages ?? [],
  }
}

export interface Waterfall {
  _meta?: DocumentMeta;
  id: string;
  versions: Version[]
  orgIds: string[]; // Orgs linked to waterfall, can read documents if in it
  documents: WaterfallFile[];
  sources: WaterfallSource[];
  rightholders: WaterfallRightholder[];
  expenseTypes: Record<string, ExpenseType[]>; // key is contractId or 'directSales'
  mainCurrency: MovieCurrency;
  rca?: WaterfallRcaData;
  template: WaterfallTemplate;
  mode: WaterfallMode;
  /**
   * Node positions saved when the user moves them by hand in the graph builder.
   * Keyed by version id, then by node id. Display only: never read by the waterfall
   * calculation. See issue #10286.
   */
  layout?: Record<string, Record<string, { x: number; y: number }>>;
}

export type WaterfallAndMovie = Waterfall & { movie?: Movie };

export function createWaterfall(params: Partial<Waterfall> = {}): Waterfall {
  return {
    id: '',
    versions: [],
    orgIds: [],
    documents: [],
    expenseTypes: {},
    ...params,
    rightholders: params.rightholders?.map(r => createWaterfallRightholder(r)) ?? [],
    sources: params.sources?.map(s => createWaterfallSource(s)) ?? [],
    mainCurrency: params.mainCurrency ?? 'EUR',
    template: createWaterfallTemplate(params.template),
    mode: params.mode ?? 'default',
  }
}

export function createWaterfallRightholder(params: Partial<WaterfallRightholder> = {}): WaterfallRightholder {
  return {
    id: '',
    name: '',
    roles: [],
    color: defaultColor,
    ...params,
  }
}

export function isProducer(rightholder: WaterfallRightholder) {
  return rightholder.roles.includes('producer');
}

export function getProducers(waterfall: Waterfall) {
  return waterfall.rightholders.filter(isProducer);
}

export function getProducer(waterfall: Waterfall) {
  const producers = getProducers(waterfall);
  return producers.length ? producers[0] : null;
}

export function createWaterfallDocument<Meta extends WaterfallDocumentMeta>(params: Partial<WaterfallDocument<Meta>> = {}): WaterfallDocument<Meta> {

  const toObject = () => {
    if (isContract(params)) return createWaterfallContract({ ...params.meta, status: 'accepted' }) as Meta;
    if (isBudget(params)) return createWaterfallBudget(params.meta) as Meta;
    if (isFinancingPlan(params)) return createWaterfallFinancingPlan(params.meta) as Meta;
  };

  const meta = toObject();
  delete (meta as any).id;
  delete (meta as any)._meta;
  delete (meta as any).rootId;
  delete (meta as any).signatureDate;
  delete (meta as any).titleId;
  delete (meta as any).name;


  return {
    _meta: (params.meta as any)._meta || createDocumentMeta({ createdAt: new Date() }),
    id: (params.meta as any).id ?? '',
    type: 'contract',
    name: (params.meta as any).name ?? '',
    waterfallId: '',
    ownerId: '',
    rootId: (params.meta as any).rootId ?? '',
    signatureDate: (params.meta as any).signatureDate ?? new Date(),
    ...params,
    meta,
  };
}

export function createWaterfallContract(params: Partial<WaterfallContract>): WaterfallContract {
  return {
    _meta: createDocumentMeta({}),
    id: '',
    name: '',
    titleId: '',
    termIds: [],
    buyerId: '',
    sellerId: '',
    type: 'mainDistributor',
    status: 'pending',
    stakeholders: [],
    rootId: '',
    price: { value: 0 },
    ...params
  }
}

export function createWaterfallBudget(params: Partial<WaterfallBudget>): WaterfallBudget {
  return {
    _meta: createDocumentMeta({}),
    id: '',
    ...params
  }
}

export function createWaterfallFinancingPlan(params: Partial<WaterfallFinancingPlan>): WaterfallFinancingPlan {
  return {
    _meta: createDocumentMeta({}),
    id: '',
    ...params
  }
}

export const isContract = (document: Partial<WaterfallDocument>): document is WaterfallDocument<WaterfallContract> => document?.type === 'contract';
export const isBudget = (document: Partial<WaterfallDocument>): document is WaterfallDocument<WaterfallBudget> => document?.type === 'budget';
export const isFinancingPlan = (document: Partial<WaterfallDocument>): document is WaterfallDocument<WaterfallFinancingPlan> => document?.type === 'financingPlan';

export function isWaterfallSale(contract: Partial<WaterfallContract>): contract is WaterfallSale {
  return Object.keys(rightholderGroups.sales).includes(contract.type);
}

export function convertDocumentTo<T>(document: WaterfallDocument): T {
  switch (document.type) {
    case 'contract':
      return {
        id: document.id,
        rootId: document.rootId,
        signatureDate: document.signatureDate,
        titleId: document.waterfallId,
        name: document.name,
        ocr: document.ocr || null,
        ...document.meta as T,
        _meta: document._meta
      };
    case 'budget':
      return {
        id: document.id,
        ...document.meta as T,
        _meta: document._meta
      };
    case 'financingPlan':
      return {
        id: document.id,
        ...document.meta as T,
        _meta: document._meta
      };
    default:
      break;
  }
}

export type WaterfallDocumentMeta = WaterfallBudget | WaterfallContract | WaterfallFinancingPlan;

interface DocumentMetaWithDeleteConfig extends DocumentMeta {
  skipIncomesDeletion?: boolean;
}

export interface WaterfallDocument<Meta extends (WaterfallDocumentMeta | unknown) = unknown> {
  _meta?: DocumentMetaWithDeleteConfig;
  id: string; // Same as the WaterfallFile id
  /** If document is an amendment, provide root document Id */
  rootId: string;
  name: string;
  signatureDate?: Date;
  type: 'financingPlan' | 'budget' | 'contract';
  waterfallId: string; // Parent document Id
  ownerId: string; // Uploader orgId
  sharedWith?: string[]; // OrgIds that can read this document
  meta: Meta;
  rca?: WaterfallContractRcaData;
  ocr?: OcrData;
}

export interface WaterfallBudget {
  _meta?: DocumentMeta;
  id: string; // Same as the WaterfallFile id
}

export interface WaterfallContractPaymentDetails {
  value: number;
  date: Date;
}

export interface WaterfallContractPriceReference {
  value: number;
  currency?: MovieCurrency;
}

export interface WaterfallContractPrice {
  value: number;
  paymentDetails?: WaterfallContractPaymentDetails[] // #10052 list of payments with date. Used only for "interest" condition
  reference?: WaterfallContractPriceReference; // #10253 Only informational data & not used for calculations 
  // TODO #10129 for future use : useForFinancePlan?: boolean;
}

export interface WaterfallContract extends Omit<BaseContract, 'currency'> {
  type: RightholderRole;
  name: string;
  price: WaterfallContractPrice;
  ocr?: OcrData;
}

export const invertedRoles: RightholderRole[] = ['author', 'agent']; // Roles for which the licensor and licensee roles are inverted in a contract

export interface WaterfallSale extends WaterfallContract {
  type: keyof typeof rightholderGroups.sales;
}

export interface WaterfallFullSale extends WaterfallSale {
  terms: Term[];
}

export interface WaterfallFinancingPlan {
  _meta?: DocumentMeta;
  id: string; // Same as the WaterfallFile id
}
